import React, { useState, useEffect } from "react";
import BackgroundAnimation from "../components/BackgroundAnimation";
import TileEditor from "../components/TileEditor";
import DeleteTileModal from "../components/DeleteTileModal";
import TileDetailModal from "../components/TileDetailModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import TileSkeletonGrid from "../components/TileSkeletonGrid"; 
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { useTiles } from "../hooks/useTiles";
import { useTileOperations } from "../hooks/useTileOperations";
import { useAuth } from "../hooks/useAuth";
import { PlusCircle, TrashIcon, Settings, Key, Move, Save, X } from "lucide-react";
import TileCard from "../components/TileCard";
import DraggableTileCard from "../components/DraggableTileCard";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { tilesAPI } from "../utils/apiClient";

function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, changePassword } = useAuth();
  const { tiles, loading, error, refetch } = useTiles();
  const { deleting, deleteTile } = useTileOperations();
  const [activeTab, setActiveTab] = useState('tiles');

  // Use auth user if available, fallback to location state
  const user = authUser || location.state?.user;

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [selectedTile, setSelectedTile] = useState(null);
  const [editingTile, setEditingTile] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [isArrangeMode, setIsArrangeMode] = useState(false);
  const [arrangedTiles, setArrangedTiles] = useState([]);
  const [isSavingArrangement, setIsSavingArrangement] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize arranged tiles when tiles load or arrange mode changes
  useEffect(() => {
    if (tiles && tiles.length > 0) {
      setArrangedTiles([...tiles]);
    }
  }, [tiles]);

  // If no user data is present and not authenticated, redirect to login
  if (!user && !isAuthenticated) {
    return <Navigate to="/ctshy-login" replace />;
  }

  const handleCreateTile = () => {
    setEditingTile(null);
    setIsEditorOpen(true);
  };

  const handleEditTile = (tile) => {
    setEditingTile(tile);
    setIsEditorOpen(true);
  };

  const handleSaveTile = (response) => {
    console.log("Tile saved:", response);
    setIsEditorOpen(false);
    setEditingTile(null);
    refetch(); // Refresh the tiles list
  };

  const handleCancelEdit = () => {
    setIsEditorOpen(false);
    setEditingTile(null);
  };

  const handleDeleteTile = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async (tileToDelete) => {
    try {
      await deleteTile(tileToDelete._id);
      setIsDeleteModalOpen(false);
      refetch(); // Refresh the tiles list
    } catch (error) {
      console.error("Error deleting tile:", error);
      toast.error(error.response?.error || error.message);
    }
  };

  const handleTileClick = (tile) => {
    setSelectedTile(tile);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTile(null);
  };

  const handleChangePassword = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handleCloseChangePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
  };

  const handlePasswordChange = async (passwordData) => {
    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success("Password changed successfully!");
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.error || error.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setArrangedTiles((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleArrangeMode = () => {
    if (isArrangeMode) {
      // Cancel arrange mode - reset to original order
      setArrangedTiles([...tiles]);
      setIsArrangeMode(false);
    } else {
      // Enter arrange mode
      setIsArrangeMode(true);
    }
  };

  const handleSaveArrangement = async () => {
    setIsSavingArrangement(true);
    try {
      // Create array of tiles with their new order
      const tilesWithOrder = arrangedTiles.map((tile, index) => ({
        id: tile._id,
        order: index
      }));

      await tilesAPI.reorderTiles(tilesWithOrder);
      toast.success("Tile arrangement saved!");
      setIsArrangeMode(false);
      refetch(); // Refresh tiles to get the new order from server
    } catch (error) {
      console.error("Error saving arrangement:", error);
      toast.error(error.error || "Failed to save tile arrangement");
    } finally {
      setIsSavingArrangement(false);
    }
  };

  // Loading state with skeleton grid
  if (loading) {
    return (
      <>
        <BackgroundAnimation />
        <div className="dashboard-container">
          <div className="user-info">
            <div className="flex items-center justify-between">
              <span>Welcome, {user?.username}</span>
            </div>
          </div>
          
          {user?.isAdmin && (
            <div className="flex gap-4 items-end justify-start mt-6 flex-wrap">
              <button
                disabled
                className="flex items-center gap-2 bg-violet-600/50 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusCircle className="w-5 h-5" />
                Create New Tile
              </button>

              <button
                disabled
                className="flex items-center gap-2 bg-red-600/50 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <TrashIcon className="w-5 h-5" />
                Delete Tile
              </button>
            </div>
          )}

          <div className="tile-gridd">
            <TileSkeletonGrid />
          </div>
        </div>
      </>
    );
  }

  // Error state
if (error) {
  return (
    <>
      <BackgroundAnimation />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-800 to-indigo-900">
        <div className="
          bg-[rgba(138,79,255,0.1)]
          border border-[#8A4FFF]
          rounded-xl
          p-8
          w-[500px] max-w-[90%]
          shadow-[0_5px_15px_rgba(0,0,0,0.5)]
          text-white
          z-[200]
          backdrop-blur-[10px]
          backdrop-saturate-150
          flex flex-col items-center space-y-4
          animate-fadeIn
        ">
          <h2 className="text-xl font-semibold">Oops! Something went wrong 😓</h2>
          <p className="text-sm text-center">Error loading tiles: <span className="text-pink-300">{error.message}</span></p>

          <button
            onClick={refetch}
            className="
              mt-4 px-5 py-2 rounded-lg
              border border-white
              hover:bg-white hover:text-purple-700
              transition duration-300
              font-medium
            "
          >
            🔄 Retry
          </button>
        </div>
      </div>
    </>
  );
}


  return (
    <>
      <BackgroundAnimation />
      <div className="dashboard-container">
        <div className="user-info">
          <div className="flex items-center justify-between">
            <span>Welcome, {user?.username}</span>
            <button
              onClick={handleChangePassword}
              className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-full shadow-sm transition absolute right-8 top-4"
            >
              <Key className="w-4 h-4" />
              {/* Change Password */}
            </button>
          </div>
          <div className="flex gap-6 mt-4">
            <button
              onClick={() => setActiveTab('tiles')}
              className={`text-lg font-medium transition-colors ${
                activeTab === 'tiles' 
                  ? 'text-violet-400 border-b-2 border-violet-400' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Tiles
            </button>
            <button
              onClick={() => navigate('/fund', { state: { user } })}
              className={`text-lg font-medium transition-colors ${
                activeTab === 'fund' 
                  ? 'text-violet-400 border-b-2 border-violet-400' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Fund
            </button>
          </div>
        </div>

        {user.isAdmin && (
          <div className="flex gap-4 items-end justify-start mt-6 flex-wrap">
            {!isArrangeMode ? (
              <>
                <button
                  onClick={handleCreateTile}
                  className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusCircle className="w-5 h-5" />
                  Create New Tile
                </button>

                <button
                  onClick={handleDeleteTile}
                  disabled={tiles.length === 0}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="w-5 h-5" />
                  Delete Tile
                </button>

                <button
                  onClick={handleToggleArrangeMode}
                  disabled={tiles.length === 0}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Move className="w-5 h-5" />
                  Arrange Tiles
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSaveArrangement}
                  disabled={isSavingArrangement}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  {isSavingArrangement ? "Saving..." : "Save Arrangement"}
                </button>

                <button
                  onClick={handleToggleArrangeMode}
                  disabled={isSavingArrangement}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-xl shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              </>
            )}
          </div>
        )}

        <div className="tile-gridd">
          {tiles.length === 0 ? (
            <div className="no-tiles-message">
              <p>No tiles created yet.</p>
              <button
                onClick={handleCreateTile}
                className="create-first-tile-button"
              >
                Create your first tile
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={arrangedTiles.map(t => t._id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
                  {arrangedTiles.map((tile) => (
                    <DraggableTileCard
                      key={tile._id}
                      tile={tile}
                      onTileClick={handleTileClick}
                      onEditTile={handleEditTile}
                      isArrangeMode={isArrangeMode}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {isEditorOpen && (
          <div className="modal-overlay">
            <TileEditor
              onSave={handleSaveTile}
              onCancel={handleCancelEdit}
              editingTile={editingTile}
            />
          </div>
        )}

        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <DeleteTileModal
              tiles={tiles}
              onDelete={handleConfirmDelete}
              onCancel={handleCancelDelete}
              isDeleting={deleting}
            />
          </div>
        )}

        {isDetailModalOpen && selectedTile && (
          <div className="modal-overlay">
            <TileDetailModal
              tile={selectedTile}
              onClose={handleCloseDetailModal}
              onEdit={() => {
                handleCloseDetailModal();
                handleEditTile(selectedTile);
              }}
            />
          </div>
        )}

        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={handleCloseChangePasswordModal}
          onChangePassword={handlePasswordChange}
          isChanging={changingPassword}
        />
      </div>
    </>
  );
}

export default DashboardPage;